import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import format from 'date-fns/format';
import { capitalizeFirstLetter } from '@avangard/utils/formatters';
import { BaseEntity, Entity as DecribeEntity, Relation } from '@avangard/utils/entity';

import { i18n, getLocaleForDateFns } from '@lib/i18n';
import { CourseEntity } from '@modules/courses/entities';
import { programTypeTranslates } from '@modules/lap/constants';
import { LapPlatoonsItem, Platform } from '@modules/types/graphql';

import type { Lap, LapProgramType } from '@modules/types/graphql';

export type Entity = Partial<Lap> | null;

type getFormattedNameOptions = {
    withPlatform?: boolean;
    withLeadDot?: boolean;
};

@DecribeEntity('LapEntity')
class LapEntity extends BaseEntity {
    id: string;
    name: string;
    from: string;
    to: string;
    platform: Platform | null;
    isCurrent: boolean;
    programType: LapProgramType | null;
    companyCount: number;
    platoonCount: number;
    platoons: LapPlatoonsItem[];
    isDynamicPlatoons: boolean;

    @Relation(() => CourseEntity)
    course: CourseEntity;

    constructor(lap: Entity) {
        super(lap);

        this.id = lap?.id ?? '';
        this.name = lap?.name ?? '';
        this.from = lap?.from ?? '';
        this.to = lap?.to ?? '';
        this.platform = lap?.platform ?? null;
        this.isCurrent = lap?.isCurrent ?? false;
        this.programType = lap?.programType ?? null;
        this.companyCount = lap?.companyCount ?? 0;
        this.platoonCount = lap?.platoonCount ?? 0;
        this.platoons = lap?.platoons ?? [];
        this.isDynamicPlatoons = this.platoons.length > 0;
    }

    get maxCompany() {
        if (this.isDynamicPlatoons) {
            const company = this.platoons.reduce(
                (carry, platoon) => Math.max(carry, platoon.company),
                -Infinity,
            );

            return company;
        }

        return this.companyCount;
    }

    get maxPlatoon() {
        if (this.isDynamicPlatoons) {
            const platoon = this.platoons.reduce(
                (carry, platoon) => Math.max(carry, platoon.platoon),
                -Infinity,
            );

            return platoon;
        }

        return this.platoonCount;
    }

    get platoonXCount() {
        if (this.isDynamicPlatoons) {
            const platoonX = this.platoons.reduce(
                (carry, platoon) => Math.max(carry, platoon.platoonX),
                -Infinity,
            );

            return platoonX;
        }

        if (!this.companyCount || !this.platoonCount) {
            return 0;
        }

        return this.companyCount * this.platoonCount;
    }

    exists() {
        const exists = this.id !== '';

        return exists;
    }

    getFromPlatoonId(platoonId: string): LapPlatoonsItem {
        const platoon = this.platoons.find(platoonItem => platoonItem.id === platoonId);

        return platoon as unknown as LapPlatoonsItem;
    }

    getGroupedPlatoonsByCompany() {
        const result = this.platoons.reduce<Record<number, LapPlatoonsItem[]>>((carry, platoon) => {
            if (!carry[platoon.company]) {
                carry[platoon.company] = [];
            }

            carry[platoon.company].push(platoon);

            return carry;
        }, {});

        return result;
    }

    getProgramType() {
        if (!this.programType) {
            return '';
        }

        const programType = programTypeTranslates[this.programType];

        return i18n.t(programType);
    }

    getPlatformTitle() {
        if (!this.platform) {
            return null;
        }

        const title = capitalizeFirstLetter(this.platform);

        return title;
    }

    getFullDate() {
        const from = this.getFromDate();
        const to = this.getToDate();

        const date = `${from} - ${to}`;

        return date;
    }

    getFromDate() {
        const fromDate = this.from ? new Date(this.from) : null;
        const locale = getLocaleForDateFns();

        const from = fromDate ? format(fromDate, 'dd.MM.yyyy', { locale }) : '//';

        return from;
    }

    getToDate() {
        const toDate = this.from ? new Date(this.to) : null;
        const locale = getLocaleForDateFns();

        const to = toDate ? format(toDate, 'dd.MM.yyyy', { locale }) : '//';

        return to;
    }

    getDate(dateString: string): Date | null {
        if (!dateString) {
            return null;
        }

        const date = new Date(dateString);

        return date;
    }

    nowDateBefore() {
        if (!this.from) {
            return false;
        }

        const now = new Date().getTime();
        const from = new Date(this.from).getTime();

        return now < from;
    }

    nowDateIncluded() {
        if (!this.from || !this.to) {
            return false;
        }

        const now = new Date().getTime();
        const from = new Date(this.from).getTime();
        const to = new Date(this.to).getTime();

        return from <= now && now <= to;
    }

    nowDateAfter() {
        if (!this.to) {
            return false;
        }

        const now = new Date().getTime();
        const to = new Date(this.to).getTime();

        return now > to;
    }

    hasCurrentDateInLap() {
        return this.nowDateIncluded() || this.nowDateAfter();
    }

    getDaysList() {
        const start = this.from ? new Date(this.from) : null;
        const end = this.to ? new Date(this.to) : null;

        if (!start || !end) {
            return [];
        }

        const interval = eachDayOfInterval({ start, end });

        const list = interval.map(date => ({
            date: format(date, 'yyyy-MM-dd'),
            formattedDate: format(date, 'EEEEEE, dd.MM.yyyy', { locale: getLocaleForDateFns() }),
        }));

        return list;
    }

    isActiveLap(currentLap: LapEntity) {
        if (!currentLap.exists() || !this.id) {
            return false;
        }

        const isActiveLap = currentLap.id === this.id;

        return isActiveLap;
    }

    isCenterLap() {
        const isCenterLap = this.platform === Platform.center;

        return isCenterLap;
    }

    isOnlineLap() {
        const isOnlineLap = this.platform === Platform.online;

        return isOnlineLap;
    }

    getFormattedName(options: getFormattedNameOptions = {}) {
        const { withPlatform = true, withLeadDot = true } = options;

        const platform = this.getPlatformTitle();
        const courseName = this.course.name;
        const platformTitle = this.getProgramType();

        let name = this.name;

        if (platform && withPlatform) {
            name += ` • ${platform}`;
        }

        if (courseName || platformTitle) {
            name += ` • ${courseName || platformTitle}`;
        }

        if (!withLeadDot) {
            return name.replace(/•/, '').trim();
        }

        return name.trim();
    }
}

export { LapEntity };
